// Parses the transcript formats that reach Ops datasets into speaker turns, and splits
// turn text into highlight segments. Pure, so the view stays presentational.
//
// Formats seen in real data:
//   [AI]: text / [Caller]: text              AI voice agent calls
//   [SPEAKER_00]: text                       diarized calls without role labels
//   [00:04] Front Desk: text                 timestamped fixtures
//   plain lines with no speaker              some outbound calls
// Agent text can carry tone tags ("[Enthusiastic] Yes…"), and "..." marks a silent turn.

/** Which side of the conversation a speaker is on. `none`: the transcript has no speakers. */
export type TranscriptSide = 'office' | 'caller' | 'none'

export type TranscriptTurn = {
  /** Display label, e.g. "AI", "Caller", "Speaker 1"; null when the transcript has none. */
  speaker: string | null
  side: TranscriptSide
  /** Timestamp as written in the transcript, when it has one. */
  time?: string
  /** Empty for a silent turn. */
  text: string
  /** Consecutive silent turns from the same speaker, collapsed. 0 for speech. */
  silentCount: number
}

export type ParsedTranscript = {
  turns: TranscriptTurn[]
  speakers: { label: string; side: TranscriptSide }[]
}

const TIMED_LINE = /^\[(\d{1,2}:\d{2}(?::\d{2})?)\]\s*([^:[\]]{1,40}):\s*(.*)$/
const LABELED_LINE = /^\[([^\]]{1,40})\]:\s*(.*)$/
const SILENCE = /^(\.{2,}|…)$/
const OFFICE_LABEL = /^(ai|agent|assistant|bot|office|front ?desk|staff|reception(ist)?|practice|clinic)$/i
const CALLER_LABEL = /^(caller|customer|patient|client|user)$/i
const DIARIZED_LABEL = /^speaker[_ ]?(\d+)$/i

/** Diarized labels are zero-based ("SPEAKER_00"); people count from one. */
function displayLabel(raw: string): string {
  const diarized = DIARIZED_LABEL.exec(raw.trim())
  return diarized ? `Speaker ${Number(diarized[1]) + 1}` : raw.trim()
}

export function parseTranscript(text: string): ParsedTranscript {
  const lines = text
    .replace(/\r/g, ``)
    .split(`\n`)
    .map((line) => line.trim())
    .filter(Boolean)

  const parsed = lines.map((line) => {
    const timed = TIMED_LINE.exec(line)
    if (timed) return { speaker: displayLabel(timed[2]), time: timed[1], text: timed[3].trim() }
    const labeled = LABELED_LINE.exec(line)
    if (labeled) return { speaker: displayLabel(labeled[1]), time: undefined, text: labeled[2].trim() }
    return { speaker: null, time: undefined, text: line }
  })
  const hasSpeakers = parsed.some((line) => line.speaker !== null)

  // Known role labels keep their side. An unknown label takes the office side if no
  // office speaker is known yet (the first voice on a call is usually the one answering),
  // and the caller side otherwise.
  const speakers: ParsedTranscript[`speakers`] = []
  const sideOf = new Map<string, TranscriptSide>()
  for (const { speaker } of parsed) {
    if (speaker === null || sideOf.has(speaker)) continue
    const side: TranscriptSide = OFFICE_LABEL.test(speaker)
      ? `office`
      : CALLER_LABEL.test(speaker)
        ? `caller`
        : [...sideOf.values()].includes(`office`)
          ? `caller`
          : `office`
    sideOf.set(speaker, side)
    speakers.push({ label: speaker, side })
  }

  const turns: TranscriptTurn[] = []
  for (const line of parsed) {
    const silent = SILENCE.test(line.text)
    const previous = turns[turns.length - 1]
    // In a speaker transcript an unlabeled line continues the turn before it.
    const speaker = line.speaker ?? (hasSpeakers ? (previous?.speaker ?? null) : null)
    const sameSpeaker = hasSpeakers && previous !== undefined && previous.speaker === speaker

    if (silent && sameSpeaker && previous.silentCount > 0) {
      previous.silentCount += 1
    } else if (!silent && sameSpeaker && previous.silentCount === 0) {
      previous.text = `${previous.text} ${line.text}`
    } else {
      turns.push({
        speaker,
        side: speaker === null ? `none` : (sideOf.get(speaker) ?? `office`),
        time: line.time,
        text: silent ? `` : line.text,
        silentCount: silent ? 1 : 0,
      })
    }
  }
  return { turns, speakers }
}

export type SegmentKind = 'plain' | 'tone' | 'name' | 'family' | 'cue' | 'match'
export type TextSegment = { text: string; kind: SegmentKind }

/** Words a People-stage decision hinges on: who the call is about, relative to the caller. */
const FAMILY_WORDS = [
  `son`,
  `sons`,
  `daughter`,
  `daughters`,
  `child`,
  `children`,
  `kid`,
  `kids`,
  `baby`,
  `wife`,
  `husband`,
  `spouse`,
  `partner`,
  `mom`,
  `mother`,
  `dad`,
  `father`,
  `parent`,
  `parents`,
  `brother`,
  `sister`,
  `grandson`,
  `granddaughter`,
  `grandchild`,
  `grandchildren`,
  `grandmother`,
  `grandma`,
  `grandfather`,
  `grandpa`,
  `stepson`,
  `stepdaughter`,
  `guardian`,
  `custody`,
]

/** Phrases that decide a person's patient type: new, existing, or not a patient here. */
const PATIENT_TYPE_CUES = [
  `new patients?`,
  `existing patients?`,
  `current patients?`,
  `first (?:visit|time|appointment|checkup)`,
  `never been (?:to|in|here)`,
  `been (?:to|in) (?:your|our|the) office before`,
  `been here before`,
  `(?:has|have) seen you`,
  `re-?schedul(?:e|ing)`,
  `wrong (?:number|office)`,
]

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, `\\$&`)

/**
 * Splits turn text into segments for highlighting: tone tags, the search query, names
 * from the answer key, family words, and patient-type cues. Earlier kinds win where they
 * overlap.
 */
export function segmentText(text: string, options: { names?: string[]; query?: string } = {}): TextSegment[] {
  const nameParts = [
    ...new Set(
      (options.names ?? [])
        .flatMap((name) => name.split(/\s+/))
        .map((part) => part.replace(/[^\p{L}'-]/gu, ``))
        .filter((part) => part.length >= 2 && part.toLowerCase() !== `unknown`),
    ),
  ]
  const query = options.query?.trim()
  const alternatives = [
    `(?<tone>\\[[A-Za-z][A-Za-z ]{1,24}\\])`,
    query ? `(?<match>${escapeRegExp(query)})` : null,
    nameParts.length ? `(?<name>\\b(?:${nameParts.map(escapeRegExp).join(`|`)})\\b)` : null,
    `(?<family>\\b(?:${FAMILY_WORDS.join(`|`)})\\b)`,
    `(?<cue>\\b(?:${PATIENT_TYPE_CUES.join(`|`)})\\b)`,
  ].filter(Boolean)
  const pattern = new RegExp(alternatives.join(`|`), `giu`)

  const segments: TextSegment[] = []
  let cursor = 0
  for (const found of text.matchAll(pattern)) {
    const index = found.index ?? 0
    if (index > cursor) segments.push({ text: text.slice(cursor, index), kind: `plain` })
    const groups = found.groups ?? {}
    const kind: SegmentKind = groups.tone
      ? `tone`
      : groups.match
        ? `match`
        : groups.name
          ? `name`
          : groups.family
            ? `family`
            : `cue`
    segments.push({ text: found[0], kind })
    cursor = index + found[0].length
  }
  if (cursor < text.length) segments.push({ text: text.slice(cursor), kind: `plain` })
  return segments
}
